/**
 * The weight-related conditions insurers accept toward ESG coverage (BAR-2929).
 *
 * Captured on the PN consultation card's qualification beat as a multi-chip, and
 * read by the insurance section's likely-eligibility line (BAR-2919): Cigna at
 * BMI 30–34.9, other private insurers at 35–39.9, need one of these.
 *
 * Stored in the note's answers under `WEIGHT_RELATED_CONDITIONS_ID`, like every
 * other answer on the card. Whether they should also become `Condition`
 * resources, so the prior-auth team can reuse them, is an open question on the
 * spec; until it is settled the note is the only record.
 */

import { QuestionnaireResponse } from '@medplum/fhirtypes';
import { readEligibilityConditionCodes } from './eligibility-intake';

export const WEIGHT_RELATED_CONDITIONS = [
  'Type 2 diabetes',
  'Poorly controlled hypertension',
  'Hyperlipidemia',
  'Coronary artery or cardiovascular disease',
  'Obstructive sleep apnea',
  'Pulmonary hypertension',
  'MASLD/MASH or other obesity-related fatty liver disease',
  'Weight-bearing joint disease',
  'Lower-extremity lymphatic or venous obstruction',
  'Medically refractory GERD',
] as const;
export type WeightRelatedCondition = (typeof WEIGHT_RELATED_CONDITIONS)[number];

/** The card's answer id for the selected conditions, a `string[]`. */
export const WEIGHT_RELATED_CONDITIONS_ID = 'ql_conditions';

/**
 * The selected conditions, in list order. A value outside the list is dropped
 * rather than counted, so a renamed option can never make a patient look
 * eligible on a condition nobody confirmed.
 */
export function readWeightRelatedConditions(values: Record<string, unknown>): WeightRelatedCondition[] {
  const selected = values[WEIGHT_RELATED_CONDITIONS_ID];
  if (!Array.isArray(selected)) return [];

  return WEIGHT_RELATED_CONDITIONS.filter((condition) => selected.includes(condition));
}

export function hasWeightRelatedCondition(values: Record<string, unknown>): boolean {
  return readWeightRelatedConditions(values).length > 0;
}

/**
 * Signup's "Do any of these apply?" codes that name an insurer condition. High
 * blood pressure does not map: the insurer condition is "poorly controlled"
 * hypertension, which signup cannot tell, so the navigator asks.
 */
const CONDITION_BY_SIGNUP_CODE: Record<string, WeightRelatedCondition> = {
  diabetes: 'Type 2 diabetes',
  high_cholesterol: 'Hyperlipidemia',
  sleep_apnea: 'Obstructive sleep apnea',
  fatty_liver_disease: 'MASLD/MASH or other obesity-related fatty liver disease',
};

/**
 * The conditions signup's answers prefill on the PN card, in list order. Signup
 * asks only at BMI 27–29.9, so most patients have none.
 */
export function readSignupWeightRelatedConditions(
  response: QuestionnaireResponse | undefined
): WeightRelatedCondition[] {
  const mapped = readEligibilityConditionCodes(response).map((code) => CONDITION_BY_SIGNUP_CODE[code]);

  return WEIGHT_RELATED_CONDITIONS.filter((condition) => mapped.includes(condition));
}
